import { get, put, post } from 'aws-amplify/api';
import { processResponse } from './api_util';
import useSWR, { mutate } from 'swr';

// Internal per-case COST (AI + AWS). Super-admin only — gated server-side on
// LivisiblyAdminApi. Never surface to customer-tier users.

// Snapshot-derived per-case cost for a month range — the SAME number the system
// view shows for this case. start/end omitted => all-time.
async function getCaseCost(caseId, start, end) {
  let path = `case_cost/case/${caseId}`;
  if (start && end) {
    path += `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  }
  const payload = { apiName: 'LivisiblyAdminApi', path };
  const response = await get(payload).response;
  return processResponse(response);
}

export function useGetCaseCost(caseId, start = null, end = null, shouldFetch = true) {
  return useSWR(
    shouldFetch && caseId ? ['CASE_COST', caseId, start, end] : null,
    () => getCaseCost(caseId, start, end),
    { revalidateOnFocus: false },
  );
}

// On-demand LIVE refresh of ONE case's current-month cost (super-admin). Writes
// the snapshot server-side — idle-safe (won't wake an idle case) — then
// revalidates every cached range for this case so the view reflects it.
export async function refreshCaseCost(caseId) {
  const payload = { apiName: 'LivisiblyAdminApi', path: `refresh_cost/case/${caseId}` };
  const response = await post(payload).response;
  const result = processResponse(response);
  await mutate((key) => Array.isArray(key) && key[0] === 'CASE_COST' && key[1] === caseId);
  return result;
}

async function getSystemCost(start, end) {
  let path = 'system_cost';
  if (start && end) {
    path += `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  }
  const payload = { apiName: 'LivisiblyAdminApi', path };
  const response = await get(payload).response;
  return processResponse(response);
}

// start/end omitted (null) => lifetime (direct cost from the ledgers).
// start/end ISO dates => period spend with allocated overhead.
export function useGetSystemCost(start = null, end = null, shouldFetch = true) {
  return useSWR(
    shouldFetch ? ['SYSTEM_COST', start, end] : null,
    () => getSystemCost(start, end),
    { revalidateOnFocus: false },
  );
}

// Internal per-case PROFITABILITY (billed revenue − cost) for a month range.
// Super-admin only (gated server-side on LivisiblyAdminApi). Returns per-month
// rows + cash (FINALIZED months) / accrual (all months) subtotals, each with both
// cost bases. start/end omitted => all-time.
async function getCaseProfitability(caseId, start, end) {
  let path = `case_profitability/case/${caseId}`;
  if (start && end) {
    path += `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  }
  const payload = { apiName: 'LivisiblyAdminApi', path };
  const response = await get(payload).response;
  return processResponse(response);
}

export function useGetCaseProfitability(caseId, start = null, end = null, shouldFetch = true) {
  return useSWR(
    shouldFetch && caseId ? ['CASE_PROFITABILITY', caseId, start, end] : null,
    () => getCaseProfitability(caseId, start, end),
    { revalidateOnFocus: false },
  );
}

// System-wide PROFITABILITY across all cases (super-admin). Per-case results +
// system cash/accrual rollups. start/end omitted => all-time.
async function getSystemProfitability(start, end) {
  let path = 'system_profitability';
  if (start && end) {
    path += `?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  }
  const payload = { apiName: 'LivisiblyAdminApi', path };
  const response = await get(payload).response;
  return processResponse(response);
}

export function useGetSystemProfitability(start = null, end = null, shouldFetch = true) {
  return useSWR(
    shouldFetch ? ['SYSTEM_PROFITABILITY', start, end] : null,
    () => getSystemProfitability(start, end),
    { revalidateOnFocus: false },
  );
}

async function getRateCard() {
  const payload = { apiName: 'LivisiblyAdminApi', path: 'rate_card' };
  const response = await get(payload).response;
  return processResponse(response);
}

export function useGetRateCard(shouldFetch = true) {
  return useSWR(
    shouldFetch ? ['RATE_CARD'] : null,
    () => getRateCard(),
    { revalidateOnFocus: false },
  );
}

export function usePutRateCard() {
  return async (card, effectiveAt) => {
    const payload = {
      apiName: 'LivisiblyAdminApi',
      path: 'rate_card',
      options: { body: { card, effective_at: effectiveAt } },
    };
    const response = await put(payload).response;
    const result = processResponse(response);
    mutate(['RATE_CARD']);
    return result;
  };
}
